import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { SEARCH_MAX, SEARCH_PARAM, parseSearch } from './filters';
import styles from './ConversationSearch.module.css';

/** Quanto esperar depois da última tecla antes de buscar no servidor. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Campo de busca da lista (por nome ou telefone). O texto buscado vive na URL (`?busca=`), junto da aba:
 * recarregar a página, voltar e copiar o endereço mantêm a busca, e abrir uma conversa a preserva. A URL só
 * muda depois de uma pausa na digitação (`replace`: cada tecla não vira uma entrada no histórico).
 */
export function ConversationSearch(): ReactElement {
  const inputId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const inUrl = parseSearch(searchParams.get(SEARCH_PARAM));
  const [text, setText] = useState(inUrl);
  const input = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // O último valor que ESTE campo pôs na URL: uma mudança diferente vinda de fora (voltar, um link) é
  // refletida no campo; a que veio daqui não pode apagar o que a pessoa ainda está digitando.
  const written = useRef(inUrl);
  // A URL mais nova. A gravação pode vir de um temporizador armado várias renderizações atrás: com a URL do
  // momento em que ele foi armado, a aba escolhida nesse meio tempo seria desfeita ao gravar a busca por cima.
  const latestParams = useRef(searchParams);
  useEffect(() => {
    latestParams.current = searchParams;
  });

  const apply = (value: string): void => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const clean = parseSearch(value);
    if (clean === written.current) return;
    written.current = clean;
    const next = new URLSearchParams(latestParams.current);
    if (clean === '') next.delete(SEARCH_PARAM);
    else next.set(SEARCH_PARAM, clean);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (inUrl !== written.current) {
      written.current = inUrl;
      setText(inUrl);
    }
  }, [inUrl]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const clear = (): void => {
    setText('');
    apply('');
    input.current?.focus();
  };

  return (
    <form
      role="search"
      aria-label="Buscar conversas"
      className={styles.search}
      onSubmit={(event) => {
        event.preventDefault();
        apply(text);
      }}
    >
      <label htmlFor={inputId} className="sr-only">
        Buscar conversas por nome ou telefone
      </label>
      <input
        ref={input}
        id={inputId}
        type="search"
        className={styles.input}
        placeholder="Buscar por nome ou telefone"
        value={text}
        maxLength={SEARCH_MAX}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        enterKeyHint="search"
        onChange={(event) => {
          const value = event.target.value;
          setText(value);
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => apply(value), SEARCH_DEBOUNCE_MS);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && text !== '') {
            event.preventDefault();
            clear();
          }
        }}
      />
      {text !== '' && (
        <button type="button" className={styles.clear} aria-label="Limpar busca" onClick={clear}>
          <span aria-hidden="true">×</span>
        </button>
      )}
    </form>
  );
}
